import { Component } from '@theme/component';

const ITUNES_NS = 'http://www.itunes.com/dtds/podcast-1.0.dtd';
const MEDIA_NS = 'http://search.yahoo.com/mrss/';

const lang = document.documentElement.lang || 'en';
const dateFormatter = new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' });

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const days = Math.floor((Date.now() - d.getTime()) / 86400000);

  // Within the last 6 days → "X days ago" (0 → "Today", 1 → "Yesterday").
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 6) return `${days} days ago`;

  // 7–29 days → "X weeks ago" (rounded down).
  if (days <= 29) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }

  // 30+ days → "MMM D".
  return dateFormatter.format(d);
}

function firstChildText(parent, tagName) {
  if (!parent) return '';
  for (const child of parent.children) {
    if (child.localName === tagName && !child.prefix) {
      const text = child.textContent;
      if (text) return text.trim();
    }
  }
  return '';
}

function firstChildNS(parent, ns, localName) {
  if (!parent) return null;
  const matches = parent.getElementsByTagNameNS(ns, localName);
  for (const el of matches) {
    if (el.parentNode === parent) return el;
  }
  return matches[0] || null;
}

function resolveArtwork(item, channel) {
  const itunesItemImage = firstChildNS(item, ITUNES_NS, 'image');
  if (itunesItemImage) {
    const href = itunesItemImage.getAttribute('href');
    if (href) return href;
  }

  const mediaContents = item.getElementsByTagNameNS(MEDIA_NS, 'content');
  for (const mc of mediaContents) {
    const type = mc.getAttribute('type') || '';
    const medium = mc.getAttribute('medium') || '';
    const url = mc.getAttribute('url');
    if (url && (type.startsWith('image/') || medium === 'image')) return url;
  }

  const itunesChannelImage = firstChildNS(channel, ITUNES_NS, 'image');
  if (itunesChannelImage) {
    const href = itunesChannelImage.getAttribute('href');
    if (href) return href;
  }

  const channelImage = channel?.querySelector(':scope > image');
  if (channelImage) {
    const url = firstChildText(channelImage, 'url');
    if (url) return url;
  }

  return '';
}

/**
 * @extends Component
 */
class LatestEpisodeComponent extends Component {
  async connectedCallback() {
    super.connectedCallback();

    if (this.dataset.hydrated === 'true') return;
    this.dataset.hydrated = 'true';

    const url = this.dataset.rssUrl;
    if (!url) return this.#bail();

    let xmlText;
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error('bad status');
      xmlText = await res.text();
    } catch {
      return this.#bail();
    }

    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror')) return this.#bail();

    const channel = doc.querySelector('channel');
    const item = channel?.querySelector(':scope > item');
    if (!channel || !item) return this.#bail();

    const title = firstChildText(item, 'title');
    const pubDate = firstChildText(item, 'pubDate');
    const artwork = resolveArtwork(item, channel) || this.dataset.fallbackImage || '';

    // A merchant-chosen link override (e.g. the show on Spotify/Apple/YouTube)
    // wins. Otherwise resolve the episode's own link: many feeds (e.g.
    // Buzzsprout) leave the item <link> empty and put the permalink in <guid>,
    // so fall back to a URL guid, then a caller-provided link (the podcast
    // website), then the channel's own <link>.
    let link = this.dataset.linkOverride || '';
    if (!link) {
      link = firstChildText(item, 'link');
      if (!link) {
        const guid = firstChildText(item, 'guid');
        if (/^https?:\/\//i.test(guid)) link = guid;
      }
      if (!link) link = this.dataset.fallbackLink || firstChildText(channel, 'link');
    }

    // Only the title is required; a missing link just renders a non-clickable card.
    if (!title) return this.#bail();

    const dateLabel = formatDate(pubDate);
    const eyebrow = this.dataset.eyebrowLabel || 'Latest episode';

    const thumb = artwork
      ? `<img class="latest-episode__image" src="${escapeHtml(artwork)}" alt="" loading="lazy" decoding="async">`
      : '';

    const href = link ? ` href="${escapeHtml(link)}" target="_blank" rel="noopener"` : '';

    this.innerHTML = `
      <a class="latest-episode__card"${href}>
        <div class="latest-episode__thumb">${thumb}</div>
        <div class="latest-episode__body">
          <span class="latest-episode__eyebrow">${escapeHtml(eyebrow)}</span>
          <span class="latest-episode__title">${escapeHtml(title)}</span>
          ${dateLabel ? `<time class="latest-episode__date" datetime="${escapeHtml(pubDate)}">${escapeHtml(dateLabel)}</time>` : ''}
        </div>
      </a>
    `;
  }

  /**
   * When the feed can't be loaded (empty URL, CORS/fetch failure, bad feed),
   * remove the card on the live storefront, but keep a visible placeholder in
   * the theme editor so the block stays selectable and positionable.
   */
  #bail() {
    if (window.Shopify?.designMode) {
      const eyebrow = this.dataset.eyebrowLabel || 'Latest episode';
      this.innerHTML = `
        <a class="latest-episode__card" href="#" aria-hidden="true" tabindex="-1" onclick="return false;">
          <div class="latest-episode__thumb latest-episode__thumb--placeholder"></div>
          <div class="latest-episode__body">
            <span class="latest-episode__eyebrow">${escapeHtml(eyebrow)}</span>
            <span class="latest-episode__title">Latest episode preview</span>
            <span class="latest-episode__date">—</span>
          </div>
        </a>
      `;
      return;
    }
    this.remove();
  }
}

if (!customElements.get('latest-episode-component')) {
  customElements.define('latest-episode-component', LatestEpisodeComponent);
}
